import React, { ReactElement } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { isUploadRejected } from "../../../utils/functions/productPending";
import { uploadRejectedTextKey } from "../../../utils/functions/uploadRejected";
import {
  statusBadgeDotClass,
  statusBadgeLayoutClass,
} from "../../../utils/functions/statusBadgeWrap";

// "Upload failed" status line (flag REACT_APP_UPLOAD_PENDING_ENABLED, design
// 14/9 §5.6): the same 10 px status-line family as ProductPendingBadge, in
// the failed tone with a still (never pulsing) dot, shown in the list, the
// popup and the editor's toolbar for an upload the API's worker classified
// as deterministically rejected by the supplier (BaseLogo.uploadFailedReason).
// The tooltip carries the reason and what to do (remove + upload again).
// Renders NOTHING unless the flag is on AND the logo is rejected - the
// flag-off DOM is byte-identical to today. `wrap` (flag
// REACT_APP_LOGO_STATUS_BADGE_WRAP_ENABLED, statusBadgeWrap): the status
// cells of the list row and the popup pass it so the text wraps inside the
// cell instead of running into the next column; off = today's classes.
export function UploadFailedBadge({
  logo,
  className,
  wrap,
}: {
  logo: BaseLogo | null | undefined;
  className?: string;
  wrap?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  if (!isUploadRejected(logo)) return null;
  const reason = t(
    uploadRejectedTextKey(logo!.uploadFailedReason, logo!.uploadFailedMessage),
    { message: logo!.uploadFailedMessage ?? "" }
  );
  return (
    <Tooltip
      title={t("upload_failed_badge_tooltip", { reason })}
      placement="top"
    >
      <span
        data-testid="upload-failed-badge"
        className={`${statusBadgeLayoutClass(wrap)} text-xs font-semibold text-status-failed-1 ${
          className ?? ""
        }`}
      >
        <span
          aria-hidden="true"
          className={`h-[0.25rem] w-[0.25rem] ${statusBadgeDotClass(wrap)} rounded-full bg-status-failed-1`}
        />
        {t("upload_failed_badge")}
      </span>
    </Tooltip>
  );
}
